import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import { guidedBase } from './useGuidedResponse'
import type { GuideReviewer, GuideReviewerResult } from '../types/gotypes'

/**
 * A guide's reviewers, for a Manager (migration 000190): the people of the
 * customer who read its progress and notes, never the chat, the curation or
 * the export. Routes GET/POST /guides/:guideId/reviewers and DELETE
 * .../reviewers/:email (goapi/pkg/server/guided_reviewers.go), Manager only.
 */

export const guideReviewersKey = (customer: string, guideId: string) => ['guided', customer, 'guide', guideId, 'reviewers']

export interface UseGuideReviewersOptions {
  customer: string
  guideId: string
  /** Only a Manager reads the list. */
  enabled: boolean
}

export default function useGuideReviewers({ customer, guideId, enabled }: UseGuideReviewersOptions) {
  const queryClient = useQueryClient()
  const path = `${guidedBase(customer)}/guides/${encodeURIComponent(guideId)}/reviewers`
  const key = guideReviewersKey(customer, guideId)

  const query = useQuery<GuideReviewer[], Error>({
    queryKey: key,
    enabled: Boolean(customer && guideId && enabled),
    queryFn: async () => {
      try {
        return (await axios.get<{ reviewers: GuideReviewer[] }>(path)).data.reviewers ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  /** Name these emails. Returns one result per email, or null with error set. */
  const handleAdd = async (emails: string[]): Promise<GuideReviewerResult[] | null> => {
    setBusy(true)
    setError('')
    try {
      const { data } = await axios.post<{ results: GuideReviewerResult[] }>(path, { emails })
      void queryClient.invalidateQueries({ queryKey: key })
      return data.results ?? []
    } catch (err) {
      setError(extractErrorMessage(err))
      return null
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (email: string): Promise<boolean> => {
    setBusy(true)
    setError('')
    try {
      await axios.delete(`${path}/${encodeURIComponent(email)}`)
      queryClient.setQueryData<GuideReviewer[]>(key, (prev) => prev?.filter((r) => r.email !== email))
      return true
    } catch (err) {
      setError(extractErrorMessage(err))
      return false
    } finally {
      setBusy(false)
    }
  }

  return {
    reviewers: query.data ?? [],
    isLoading: query.isLoading && enabled,
    loadError: query.error?.message ?? '',
    busy,
    error,
    handleAdd,
    handleRemove,
  }
}
